import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Lock, AlertTriangle, Loader2, CheckCircle } from 'lucide-react';

/**
 * ZReadingDialog - For creating Z-Reading and locking the business day
 * @param {boolean} open - Whether dialog is open
 * @param {function} onClose - Called when dialog closes
 * @param {function} onConfirm - Called when Z-Reading is confirmed
 * @param {object} shiftSummary - Current shift summary data
 * @param {boolean} loading - Show loading state
 * @param {object} zReadingResult - Result after Z-Reading is created
 */
export function ZReadingDialog({ 
  open, 
  onClose, 
  onConfirm, 
  shiftSummary,
  loading = false,
  zReadingResult = null
}) {
  const [countedCash, setCountedCash] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (shiftSummary?.expected_cash) {
      setCountedCash(shiftSummary.expected_cash.toFixed(2));
    }
  }, [shiftSummary]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    
    const countedNum = parseFloat(countedCash);
    if (isNaN(countedNum) || countedNum < 0) {
      setError('Please enter a valid counted cash amount');
      return;
    }
    
    if (!confirmed) {
      setConfirmed(true);
      return;
    }
    
    onConfirm({
      countedCash: countedNum,
      notes: notes.trim(),
    });
  };

  const handleClose = () => {
    setCountedCash('');
    setNotes('');
    setError('');
    setConfirmed(false);
    onClose();
  };

  const variance = shiftSummary?.expected_cash && countedCash 
    ? parseFloat(countedCash) - shiftSummary.expected_cash 
    : 0;

  // Show success view after Z-Reading is created
  if (zReadingResult) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-lg bg-background border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-500">
              <CheckCircle className="h-5 w-5" />
              Z-Reading Complete
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              The business day has been locked. Review the summary below.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-card rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Z-Number:</span>
                <span className="text-foreground font-mono">{zReadingResult.z_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Business Date:</span>
                <span className="text-foreground">{zReadingResult.business_date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status:</span>
                <span className="text-red-600 font-semibold">LOCKED</span>
              </div>
            </div>
            
            <div className="bg-card rounded-lg p-4 space-y-2">
              <h4 className="text-sm font-semibold text-muted-foreground mb-2">Totals Summary</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Gross Sales:</span>
                  <span className="text-foreground">₱{zReadingResult.totals?.gross_sales?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Net Sales:</span>
                  <span className="text-foreground">₱{zReadingResult.totals?.net_sales?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Transactions:</span>
                  <span className="text-foreground">{zReadingResult.totals?.transaction_count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Cash Sales:</span>
                  <span className="text-foreground">₱{zReadingResult.totals?.cash_sales?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Expected Cash:</span>
                  <span className="text-foreground">₱{zReadingResult.totals?.expected_cash?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Counted Cash:</span>
                  <span className="text-foreground">₱{zReadingResult.totals?.counted_cash?.toFixed(2)}</span>
                </div>
              </div>
              <div className="flex justify-between pt-2 border-t border-border">
                <span className="text-muted-foreground">Variance:</span>
                <span className={`font-semibold ${zReadingResult.totals?.variance === 0 ? 'text-green-600' : 'text-red-600'}`}>
                  ₱{zReadingResult.totals?.variance?.toFixed(2)}
                </span>
              </div>
            </div>
            
            <p className="text-xs text-muted-foreground font-mono">Hash: {zReadingResult.hash}</p>
          </div>
          
          <DialogFooter>
            <Button 
              onClick={handleClose}
              className="bg-secondary hover:bg-secondary/90"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-background border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <Lock className="h-5 w-5 text-red-500" />
            Create Z-Reading
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Review shift totals and create Z-Reading to lock the business day.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="bg-red-900/20 border border-red-200/50 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-red-300">
                <p className="font-semibold">Warning: This action is irreversible</p>
                <p className="text-red-600 mt-1">
                  Creating a Z-Reading will permanently lock this business day. 
                  No more transactions, payments, or cash movements can be made for this date.
                </p>
              </div>
            </div>
          </div>
          
          {shiftSummary && (
            <div className="bg-card rounded-lg p-4 space-y-2">
              <h4 className="text-sm font-semibold text-muted-foreground mb-2">Shift Summary</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Starting Cash:</span>
                  <span className="text-foreground">₱{shiftSummary.starting_cash?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Cash Sales:</span>
                  <span className="text-foreground">₱{shiftSummary.cash_sales?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Cash In:</span>
                  <span className="text-foreground">₱{shiftSummary.cash_in?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Cash Out:</span>
                  <span className="text-foreground">₱{shiftSummary.cash_out?.toFixed(2)}</span>
                </div>
              </div>
              <div className="flex justify-between pt-2 border-t border-border">
                <span className="text-muted-foreground font-semibold">Expected Cash:</span>
                <span className="text-secondary font-semibold">₱{shiftSummary.expected_cash?.toFixed(2)}</span>
              </div>
            </div>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="countedCash" className="text-muted-foreground">Counted Cash</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₱</span>
              <Input
                id="countedCash"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={countedCash}
                onChange={(e) => { setCountedCash(e.target.value); setConfirmed(false); }}
                className="bg-card border-border text-foreground pl-8"
                data-testid="z-reading-counted-cash"
              />
            </div>
            {countedCash && shiftSummary?.expected_cash && (
              <p className={`text-sm ${variance === 0 ? 'text-green-600' : variance > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                Variance: ₱{variance.toFixed(2)} {variance > 0 ? '(Over)' : variance < 0 ? '(Short)' : '(Balanced)'}
              </p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-muted-foreground">Notes (Optional)</Label>
            <Textarea
              id="notes"
              placeholder="Any notes for this Z-Reading"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="bg-card border-border text-foreground resize-none"
              rows={2}
              data-testid="z-reading-notes"
            />
          </div>
          
          {error && (
            <p className="text-sm text-red-600" data-testid="z-reading-error">{error}</p>
          )}
          
          <DialogFooter className="gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleClose}
              disabled={loading}
              className="border-border text-muted-foreground"
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={loading}
              className={confirmed ? 'bg-red-600 hover:bg-red-700' : 'bg-secondary hover:bg-secondary/90'}
              data-testid="confirm-z-reading-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : confirmed ? (
                'Confirm & Lock Day'
              ) : (
                'Review Z-Reading'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default ZReadingDialog;
